
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Server, HardDrive, Database, Activity } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/components/ui/use-toast";

interface SystemMetricsCardsProps {
  latestMetrics: {
    cpu: number;
    memory: number;
    dbResponseTime: number;
    uptime: number;
  },
  isLoading: boolean;
}

const SystemMetricsCards: React.FC<SystemMetricsCardsProps> = ({ latestMetrics, isLoading }) => {
  // If data is loading, show loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array(4).fill(0).map((_, i) => (
          <Card key={i} className="bg-white shadow-sm border-l-4 border-l-gray-300">
            <CardContent className="p-4 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-section text-muted-foreground">Loading metrics...</p>
                  <p className="text-2xl font-bold">--</p>
                </div>
                <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse" />
              </div>
              <Progress value={0} className="h-2 bg-gray-200" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <Card className="bg-white shadow-sm border-l-4 border-l-primary">
        <CardContent className="p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-section text-muted-foreground">
                CPU Usage
              </p>
              <p className="text-2xl font-bold">{latestMetrics.cpu}%</p>
            </div>
            <Server className="h-10 w-10 text-primary opacity-80" />
          </div>
          <Progress 
            value={latestMetrics.cpu} 
            className="h-2" 
            indicatorClassName={`${latestMetrics.cpu > 80 ? 'bg-red-500' : latestMetrics.cpu > 60 ? 'bg-amber-500' : 'bg-primary'}`} 
          />
        </CardContent>
      </Card>
      <Card className="bg-white shadow-sm border-l-4 border-l-accent">
        <CardContent className="p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-section text-muted-foreground">
                Memory
              </p>
              <p className="text-2xl font-bold">{latestMetrics.memory}%</p>
            </div>
            <HardDrive className="h-10 w-10 text-accent opacity-80" />
          </div>
          <Progress 
            value={latestMetrics.memory} 
            className="h-2" 
            indicatorClassName={`${latestMetrics.memory > 80 ? 'bg-red-500' : latestMetrics.memory > 60 ? 'bg-amber-500' : 'bg-accent'}`} 
          />
        </CardContent>
      </Card>
      <Card className="bg-white shadow-sm border-l-4 border-l-amber-500">
        <CardContent className="p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-section text-muted-foreground">
                Database
              </p>
              <p className="text-2xl font-bold">{latestMetrics.dbResponseTime} ms</p>
            </div>
            <Database className="h-10 w-10 text-amber-500 opacity-80" />
          </div>
          <Progress 
            value={Math.min(100, (latestMetrics.dbResponseTime / 300) * 100)} 
            className="h-2" 
            indicatorClassName={`${latestMetrics.dbResponseTime > 200 ? 'bg-red-500' : latestMetrics.dbResponseTime > 100 ? 'bg-amber-500' : 'bg-amber-500'}`} 
          />
        </CardContent>
      </Card>
      <Card className="bg-white shadow-sm border-l-4 border-l-green-500">
        <CardContent className="p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-section text-muted-foreground">
                Uptime
              </p>
              <p className="text-2xl font-bold">{latestMetrics.uptime}%</p>
            </div>
            <Activity className="h-10 w-10 text-green-500 opacity-80" />
          </div>
          <Progress 
            value={latestMetrics.uptime} 
            className="h-2" 
            indicatorClassName={`${latestMetrics.uptime < 95 ? 'bg-red-500' : 'bg-green-500'}`} 
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemMetricsCards;
